<template>
  <div class="item-refund">
    <el-card
        class="item-refund-info card"
        shadow="never">
      Total refundable amount: ${{ computedRefundableAmount }}
    </el-card>
    <el-form
        ref="refundForm"
        :model="refundForm"
        :rules="formRules">
      <el-form-item prop="refundAmountInput">
        <el-input
            v-model.number="refundForm.refundAmountInput"
            type="number"
            step="0.01"
            :placeholder="inputPlaceholder"
            @input="handleManualInput">
          <template #prepend>
            <el-select
                v-model="inputType"
                style="width: 75px"
                @change="handleSelectChange">
              <el-option
                  :label="CURRENCY_SYMBOLS['USD']"
                  value="fixed" />
              <el-option
                  label="%"
                  value="rate" />
            </el-select>
          </template>
          <template #append>
            <el-button
                size="small"
                :class="{ 'refund-tax-button': refundType === 'tax' }"
                @click="refundTaxAmount()">
              Refund Tax
            </el-button>
          </template>
        </el-input>
      </el-form-item>
    </el-form>
    <el-card
        v-if="isRefundPercentage"
        class="card"
        shadow="never">
      {{ displayPrice }}
    </el-card>
  </div>
</template>

<script>
import { CURRENCY_SYMBOLS } from "@/constants";

export default {
  name: "ItemRefundAmountInput",
  props: {
    stripeOrderItem: {
      type: Object,
      default: null,
    },
    initialRefundAmount: {
      type: Number,
      default: 0,
    },
    RefundAmount: {
      type: Number,
      default: 0,
    },
    initialRefundType: {
      type: String,
      default: "partial",
    },
  },
  data() {
    return {
      refundType: "partial",
      inputType: "fixed",
      refundForm: {
        refundAmountInput: null,
      },
      rules: {
        refundAmountInput: {
          fixed: [
            {
              required: true,
              message: "Please enter a valid amount",
              trigger: "blur",
            },
            {
              validator: this.validateFixedAmount,
              trigger: ["blur", "change"],
            },
          ],
          rate: [
            {
              required: true,
              message: "Please enter a percentage",
              trigger: ["blur"],
            },
            {
              pattern: /^(?:100(?:\.0+)?|\d{1,2}(?:\.\d+)?)$/,
              message: "Please enter a valid percentage (0-100)",
              trigger: ["blur", "change"],
            },
          ],
        },
      },
      CURRENCY_SYMBOLS,
    };
  },
  computed: {
    formRules() {
      return {
        refundAmountInput:
          this.inputType === "fixed"
            ? this.rules.refundAmountInput.fixed
            : this.rules.refundAmountInput.rate,
      };
    },
    computedRefundableAmount() {
      return parseFloat(
        (
          this.stripeOrderItem.subtotal -
          this.stripeOrderItem.discountAmount -
          this.stripeOrderItem.refundSubtotal
        ).toFixed(2)
      );
    },
    isRefundPercentage() {
      return this.inputType === "rate";
    },
    inputPlaceholder() {
      return this.inputType === "fixed"
        ? "Enter amount ($)"
        : "Enter percentage (%)";
    },
    calculatePriceFromRate() {
      const price =
        (this.refundForm.refundAmountInput / 100) *
        this.computedRefundableAmount;
      return Math.round(price * 100) / 100;
    },
    displayPrice() {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });

      return formatter.format(this.calculatePriceFromRate);
    },
  },
  watch: {},
  mounted() {
    this.refundType = this.initialRefundType;
    if (this.initialRefundAmount > 0) {
      this.inputType = "fixed";
      this.refundForm.refundAmountInput = this.initialRefundAmount;
    }
  },
  methods: {
    handleManualInput(value) {
      this.refundType = "partial";

      const parts = value.split(".");
      if (parts.length > 1) {
        value = parts[0] + "." + parts[1].slice(0, 2);
      }

      this.refundForm.refundAmountInput = value;
      this.validateForm();
    },
    handleSelectChange() {
      this.refundType = "partial";
      this.refundForm.refundAmountInput = null;
      this.validateForm();
    },
    refundTaxAmount() {
      this.refundType = "tax";
      this.inputType = "fixed";
      this.$nextTick(() => {
        this.refundForm.refundAmountInput =
          this.stripeOrderItem.tax - this.stripeOrderItem.refundTax;
        this.validateForm();
      });
    },
    validateForm() {
      this.$refs.refundForm.validate((valid) => {
        if (this.inputType === "fixed") {
          this.$emit("refund-amount-update", {
            isValid: valid,
            refundAmount: Number(this.refundForm.refundAmountInput) || 0,
            refundType: this.refundType,
          });
        } else {
          this.$emit("refund-amount-update", {
            isValid: valid,
            refundAmount: this.calculatePriceFromRate,
            refundType: this.refundType,
          });
        }
      });
    },
    validateFixedAmount(rule, value, callback) {
      if (value === undefined || value === null) {
        callback();
        return;
      }

      if (value <= 0) {
        callback(new Error("Refund amount must be more than zero"));
      } else if (value > this.computedRefundableAmount) {
        callback(new Error("Refund can not exceed total refunable amount"));
      } else {
        callback();
      }
    },
  },
};
</script>

<style scoped>
.item-refund {
  display: flex;
  align-self: center;
  margin: 0px 16px;
  gap: 8px;

  .el-card {
    height: 20px;
  }
}
.item-refund-info {
  font-weight: bold;
}
.card {
  max-width: 480px;
  padding: 10px;
}
.refund-tax-button {
  color: white !important;
  background: #1998d5 !important;
}
</style>
